import { Router } from '@angular/router';
import { ElementRef } from '@angular/core';

import { Observable } from 'rxjs';
import { of } from 'rxjs/internal/observable/of';

import * as moment from 'moment';
import 'moment-timezone';

import {
    ApiResponse,
    HtmlTagsEnum,
    AuthApiService,
    CurrencyTypesEnum,
    ClientUserReportInterface,
    HttpStatusCodesEnum,
    ClientUserFilterModel,
    CurrencyModel,
    MarketDataFeedMarkupLevelTypesEnum,
    MarketDataFeedMarkupOptionTypesEnum
} from '@spa/api';

import {
    FileSizeValidator,
    FileTypeValidator,
    FileTypeValidatorKey,
    FileSizeValidatorKey,
    RequiredValidatorKey
} from '@spa/validators';

import { PanelPaths } from '../../paths';
import { ReportTableColumn, ReportTableGroupedColumn } from 'appShared/components/report-table';
import {  SelectOptionInterface } from 'appShared/components';

import { environment } from 'appEnv/environment';

import { EnumsHelper, ErrorsHelper, FilesHelper, SortingHelper, TextsHelper } from './helpers';
import { FeatureFlagStore } from './feature-flag.store';
import { ReportExtensions } from '@spa/reporting';
import { FeatureEnum } from 'appShared/enums';

export class Helper {
    private constructor() {
    }

    static textsHelper = () => TextsHelper;
    static errorsHelper = () => ErrorsHelper;
    static enumsHelper = () => EnumsHelper;
    static filesHelper = () => FilesHelper;
    static sortingHelper = () => SortingHelper;

    static createSuccessResponse<DataType>(data: DataType): ApiResponse<DataType> {
        return new ApiResponse<DataType>(HttpStatusCodesEnum.Ok, data);
    }

    static createSuccessResponseObservable<DataType>(data: DataType): Observable<ApiResponse<DataType>> {
        const response = new ApiResponse<DataType>(HttpStatusCodesEnum.Ok, data);

        return of(response);
    }

    static isDefined<T>(value: T | undefined | null): value is T {
        return <T>value !== undefined && <T>value !== null;
    }

    static getMatchAmountRegex(fourDecimals: boolean): RegExp {
        if (!fourDecimals) {
            return /^(\d(,)*)+(\.)?(\d{1,2})?$/;
        }

        return /^(\d(,)*)+(\.)?(\d{1,4})?$/;
    }

    static getFormatAmountRegex(thousandsSeparator = ' '): [RegExp, string] {
        const replace = '$1' + thousandsSeparator
        return [/(\d)(?=(\d{3})+$)/g, replace];
    }

    static getAmountValue(value: any): number {
        if (value && typeof (value) === 'string') {
            return parseFloat(Helper.getNonFormattedAmount(value));
        }

        return value;
    }

    static getNonFormattedAmount(value: string): string {
        if (value) {
            value = value.replace(/\s/g, '');
            value = value.replace(/,/g, '');
        } else {
            value = '0';
        }

        return value;
    }

    static roundTo(num: number, places: number) {
        const factor = 10 ** places;
        return Math.round(num * factor) / factor;
    }

    static formatPercentage(amount: number, minimumFractionDigits: number = 2){
        if (amount === null || !amount) {
            return '';
        }

        const result = ReportExtensions.toDecimalString(
            amount,
            4,
            minimumFractionDigits,
            false);

        return `${result}%`;
    }

    static formatDecimal(
        amount: number,
        sign?: string,
        maximumFractionDigits?: number,
        minimumFractionDigits?: number,
        formatNegative?: boolean): string {
        const result = ReportExtensions.toDecimalString(
            amount,
            maximumFractionDigits,
            minimumFractionDigits,
            formatNegative);

        if (sign) {
            return `${sign}${result}`;
        }

        return result;
    }

    static formatCurrency(
        amount: number,
        currency: string,
        maximumFractionDigits?: number,
        minimumFractionDigits?: number): string {
        const result = ReportExtensions.toDecimalString(
            amount,
            maximumFractionDigits,
            minimumFractionDigits,
            false);

        const sign = this.getCurrencySign(currency);

        if (sign) {
            return `${sign}${result}`;
        }

        return result;
    }
    static formatMultiCurrency(
        amount: number,
        currency?: string,
        maximumFractionDigits?: number,
        minimumFractionDigits?: number,
        formatNegative?: boolean): string {
        const result = ReportExtensions.toDecimalString(
            amount,
            maximumFractionDigits,
            minimumFractionDigits,
            formatNegative);

        if (currency) {
            return `${currency}${result}`;
        }

        return result;
    }

    static formatAmount(
        amount: string | number,
        sign: string,
        wantsFormatting: boolean = true,
        afterDecimalLength: number = 2,
        formatAsNegative: boolean = false
    ): string {
        if ((typeof amount === 'undefined') || !amount) {
            if (!wantsFormatting) {
                return '0';
            }

            let formattedAmount = '0.';

            for (let i = 1; i <= afterDecimalLength; i++) {
                formattedAmount += '0';
            }

            return formattedAmount;
        }

        if (typeof amount === 'number') {
            amount = amount.toString();
        }

        const minusMatch = amount.match('^\-');
        const parts = amount.replace('-', '').split('.');

        let formatted = parts[0];

        if (parts[0].length >= 4) {
            formatted = parts[0].replace(...Helper.getFormatAmountRegex());
        }

        if (wantsFormatting && (afterDecimalLength > 0)) {
            if (!parts[1]) {
                formatted += '.';

                for (let i = 1; i <= afterDecimalLength; i++) {
                    formatted += '0';
                }

            } else {
                formatted += '.' + parts[1];

                if (parts[1].length < afterDecimalLength) {
                    const lengthDifference = afterDecimalLength - parts[1].length;

                    for (let i = 1; i <= lengthDifference; i++) {
                        formatted += '0';
                    }
                }

            }
        }

        formatted = sign + formatted;

        if (!minusMatch) {
            return formatted;
        }

        return !(formatAsNegative) ? ('- ' + formatted) : ('(' + formatted + ')');
    }

    static formatNegativeValue(value: number, afterDecimal: number = 6): string {
        if (!value) {
            return Helper.formatAmount('0', '', true, afterDecimal);
        }

        if (value < 0) {
            const formattedValue = value.toString().replace('-', '');

            return ('(' + Helper.formatAmount(formattedValue, '', true, afterDecimal) + ')');
        }

        return Helper.formatAmount(value.toString(), '', true, afterDecimal);
    }

    static hasNegativeValue(value: string | number): boolean {
        if (!value) {
            return false;
        }

        if (typeof value !== 'string') {
            return /^-/.test(value.toString());
        }

        return /^\(/.test(value);
    }

    static formatDate(date: string | number | Date, dateFormatType?: string, toUTC: boolean = false): string {
        const formatted = !toUTC ? moment(date) : moment(date).utc();

        if (!formatted.isValid()) {
            return '';
        }

        const formatType = !dateFormatType ? environment.dateFormat : dateFormatType;

        return formatted.format(formatType);
    }

    static formatDateByValueOrNow = (date: string, dateFormatType?: string, toUTC: boolean = false): string => {
        if (!date) {
            return Helper.formatDate(new Date(), dateFormatType, toUTC);
        }

        return Helper.formatDate(date, dateFormatType, toUTC);
    }

    static formatDateByValueOrStartOfMonth = (date: string, dateFormatType?: string, toUTC: boolean = false): string => {
        if (date) {
            return Helper.formatDate(date, dateFormatType, toUTC);
        }

        const utcDate = toUTC ? moment().utc() : moment();

        return utcDate.startOf('months').format(dateFormatType);
    }

    static collectionFactory<T, U>(construct: new (item: T) => U, collection: T[]): U[] {
        return collection.map((collectionItem: T) => {
            return new construct(collectionItem);
        });
    }

    static validateFile(
        file: File,
        messageFunction: (controlLabel?: string) => { [key: string]: string; },
        skipTypeValidation = false
    ): string[] {
        const errors = [];

        if (file) {
            if (!skipTypeValidation && FileTypeValidator(false, file.type)(null)) {
                const typeErrorMessage = messageFunction('File')[FileTypeValidatorKey];

                errors.push(typeErrorMessage);
            }

            if (FileSizeValidator(false, file.size)(null)) {
                const sizeErrorMessage = messageFunction('File')[FileSizeValidatorKey];

                errors.push(sizeErrorMessage);
            }
        } else {
            const requiredErrorMessage = messageFunction('File')[RequiredValidatorKey];

            errors.push(requiredErrorMessage);
        }

        return errors;
    }

    static compareSimpleArrays<ArrayType>(a: ArrayType[], b: ArrayType[]): boolean {
        return a.sort() && b.sort() && (a.length === b.length) && a.every((item, index) => {
            return item === b[index];
        });
    }

    static getMarketDataFeedMarkupLevelAsOptions(
    ): SelectOptionInterface[] {
        return Object.keys(MarketDataFeedMarkupLevelTypesEnum)
        .map((item: MarketDataFeedMarkupLevelTypesEnum): SelectOptionInterface => {
            return {
                label: item,
                value: MarketDataFeedMarkupLevelTypesEnum[item],
            };
        });
    }

    static getMarketDataFeedMarkupTypeAsOptions(
    ): SelectOptionInterface[] {
        return Object.keys(MarketDataFeedMarkupOptionTypesEnum)
        .map((item: MarketDataFeedMarkupOptionTypesEnum): SelectOptionInterface => {
            return {
                label: item,
                value: MarketDataFeedMarkupOptionTypesEnum[item],
            };
        });
    }

    static getCapitalized(value: string): string {
        if (!value || (value && (value.trim() === ''))) {
            return '';
        }

        const splitValue = value.toLowerCase().split(' ');

        for (let i = 0; i < splitValue.length; i++) {
            splitValue[i] = splitValue[i].charAt(0).toUpperCase() + splitValue[i].substring(1);
        }

        return splitValue.join(' ');
    }

    static compactString(value: string): string {
        if (!value) {
            return null;
        }

        return value.replace(/ /g, '').replace(/,/g, '');
    }

    static convertSpacesDataToArray(value: string|string[]): string[] {
        if (!value){
            return [];
        }

        if (typeof value !== 'string'){
            return value;
        }

        if (value?.trim() === '') {
            return [' '];
        }

        return value.replace(/  +/g, ' ').trim().split(' ');
    }

    static getIntersectStrategyPermissionsCheck(permissions: string[], userPermissions: string[]): boolean {
        return permissions.every((permission: string) => {
            return (userPermissions.indexOf(permission) >= 0);
        });
    }

    static getUnionStrategyPermissionsCheck(permissions: string[], userPermissions: string[]): boolean {
        const unionChecks = permissions.map((permission: string) => {
            return (userPermissions.indexOf(permission) >= 0);
        });

        return unionChecks.indexOf(true) >= 0;
    }

    static navigateToAccountsView(type: number, reference: string, authApiService: AuthApiService, router: Router): boolean {
        const authUserPermissions = !authApiService.getAuthenticatedUser() ? [] :
            authApiService.getAuthenticatedUser().permissions;

        const detailsViewPermissions = Helper.enumsHelper().permissionsEnumHelper()
            .getDetailsViewPermissionsByAccountType(type);

        if (Helper.getUnionStrategyPermissionsCheck(detailsViewPermissions, authUserPermissions)) {
            router.navigate([
                PanelPaths.root + '/' +
                PanelPaths.children.accounts.path + '/' +
                PanelPaths.children.accounts.children.edit.path + '/' +
                reference + '/' +
                type + '/' +
                PanelPaths.children.accounts.children.edit.children.details.path
            ]).finally();

            return true;
        }

        return false;
    }

    static fillRowByChildColspan(row: HTMLTableRowElement): string[] {
        const child = row.firstChild as HTMLElement;

        if (child && (child.tagName.toLowerCase() === HtmlTagsEnum.Td)) {
            const colspan = (child as HTMLTableCellElement).colSpan;
            const cells = new Array(colspan);

            cells[cells.length - 1] = row.innerText;

            return cells;
        }

        return [];
    }

    static getRowContentAsArray(row: HTMLTableRowElement, filterBy: string[] = [HtmlTagsEnum.Td, HtmlTagsEnum.Th]): string[] {
        return Array.from(row.querySelectorAll(HtmlTagsEnum.All)).filter((child: HTMLElement) => {
            return filterBy.includes(child.tagName.toLowerCase());
        }).map((cell: HTMLTableCellElement) => {
            return cell.innerText;
        });
    }

    static getTextFromInnerDivs(container: ElementRef): string {
        return Array.from(container.nativeElement.children)
            .filter((element: HTMLElement) => (element.tagName.toLowerCase() === HtmlTagsEnum.Div))
            .map((element: HTMLDivElement) => element.innerText)
            .join(' | ');
    }

    static fillRowsByDataTableColumns(columns: Array<ReportTableColumn | ReportTableGroupedColumn>): [string[], string[]] {
        return columns.reduce((reducedHeaderData: [string[], string[]], column: ReportTableColumn | ReportTableGroupedColumn) => {
            if (column instanceof ReportTableColumn) {
                reducedHeaderData[0].push('');
                reducedHeaderData[1].push(column.label);

                return reducedHeaderData;
            }

            const filledMainCellsLength = column.childColumns.length;
            const filledMainCells = (new Array(filledMainCellsLength)).fill('');
            const filledMainCellsMedian = (filledMainCellsLength > 0) ? Math.floor((filledMainCells.length / 2) - 0.5) : 0;

            const filledSubCells = column.childColumns.map((child: ReportTableColumn) => child.label);

            filledMainCells[filledMainCellsMedian] = column.label;

            reducedHeaderData[0].push(...filledMainCells);
            reducedHeaderData[1].push(...filledSubCells);

            return reducedHeaderData;
        }, [[], []]);
    }

    static getCurrencySign(currency: string): string {
        switch (currency?.toUpperCase()) {
            case CurrencyTypesEnum.Usd:
                return '$';
            case CurrencyTypesEnum.Euro:
                return '€';
            case CurrencyTypesEnum.Gbp:
                return '£';
        }
    }

    static getCurrencyOptions(): SelectOptionInterface[] {
        return [
            {
                label: CurrencyTypesEnum.Euro,
                value: CurrencyTypesEnum.Euro,
            },
            {
                label: CurrencyTypesEnum.Usd,
                value: CurrencyTypesEnum.Usd,
            }
        ];
    }

    static getClientUserFullName(clientUser: ClientUserReportInterface): string {
        return [clientUser.first_name, clientUser.middle_name, clientUser.last_name].filter(name => name).join(' ');
    }

    static getLimitedFullName(text: string, maxCharacters: number = 50): string {
        const trimmedText = text.trim();

        return trimmedText.length <= maxCharacters ? trimmedText : `${trimmedText.substr(0, maxCharacters)}...`;
    }

    static getLimitedWalletAddress(text: string, maxCharacters: number = 13): string {
        const trimmedText = text.trim();

        return trimmedText.length <= maxCharacters ? trimmedText : `${trimmedText.substr(0, 6)}...${trimmedText.substr(trimmedText.length - 6, trimmedText.length - 1)}`;
    }

    static getTrimmedText(
        text: string | null | undefined,
        nullIfEmpty: boolean = false
    ): string | null {
        if (text == null) { return null; }

        const trimmed = text.trim();

        return nullIfEmpty && trimmed.length === 0 ? null : trimmed;
    }

    static getAllSelectOptions = () => {
        return [{
            label: 'All',
            value: null
        }];
    }

    static getEnumNumberValues(enumType: any): number[] {
        const values: number[] = [];

        Object.keys(enumType).forEach(key => {
            const enumMember = enumType[key];
            const isValueProperty = parseInt(enumMember, 10) >= 0;

            if (isValueProperty) {
                values.push(enumMember);
            }
        });

        return values;
    }

    static isFeatureEnabled(feature: FeatureEnum): boolean {
        return FeatureFlagStore.get(feature);
    }

    static getTimeZone(): string {
        return Intl.DateTimeFormat().resolvedOptions().timeZone;
    }

    /**
     * Resolve the operator's browser IANA timezone, falling back to UTC when it cannot be determined.
     */
    static getTimeZoneOrUtc(): string {
        try {
            return Helper.getTimeZone() || 'UTC';
        } catch {
            return 'UTC';
        }
    }

    /**
     * Convert an operator-selected local calendar date (YYYY-MM-DD) into the UTC
     * calendar date (YYYY-MM-DD) that contains the END of that local day.
     * Deactivation therefore takes effect at the end
     * of the operator-local day and never earlier — a subscription may stay active
     * up to ~24h beyond operator-local midnight (conservative drift). The browser
     * timezone is used by default, falling back to UTC.
     */
    static toUtcEndOfDayDate(localDate: string, timeZone?: string): string {
        const zone = timeZone || Helper.getTimeZoneOrUtc();

        return moment.tz(localDate, 'YYYY-MM-DD', zone)
            .endOf('day')
            .utc()
            .format('YYYY-MM-DD');
    }
}
